import {
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  withStyles,
} from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import StyledTableRow from "../../components/StyledTableRow";
import CopyTablesObject from "../../components/xlsx-export/CopyTablesObject";

const useStyle = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    paddingBottom: "2rem",
  },
  tableWrapper: {
    position: "relative",
    display: "inline-block",
    width: "fit-content",
    margin: "0 auto",
  },
  copyButtonContainer: {
    position: "absolute",
    top: "-30px",
    right: "0px",
    zIndex: 10,
  },
  repCell: {
    border: "0.5px solid lightgrey",
    padding: "5px",
    color: "inherit",
    width: '70px'
  },
  editableCell: {
    border: "2px solid #000",
    padding: "5px",
    color: "inherit",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  editingCell: {
    border: "2px solid #000",
    padding: "5px",
    maxWidth: "70px",
  },
  editLabel: {
    backgroundColor: "#fff9c4",
    border: "2px solid #000",
    borderBottom: "none",
    padding: "2px 8px",
    fontSize: "0.75rem",
    fontWeight: 600,
    textAlign: "center",
    display: "inline-block",
    position: "relative",
    top: "2px",
  },
  mainTableContainer: {
    maxHeight: "85vh",
    border: "0.5px solid lightgrey",
    boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
  },
  textField: {
    width: "100%",
    "& input": {
      textAlign: "right",
      padding: "4px 8px",
      fontSize: "0.85rem",
      fontWeight: 600,
    },
  },
}));

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "rgb(255,255,255,0.8)",
    color: "black",
    fontFamily: "roboto",
    fontSize: theme.typography.pxToRem(14),
    borderRadius: "0.5rem",
    padding: "0px",
    boxShadow: "0 0.1rem 0.1rem 0",
    marginTop: "0.1rem",
  },
}))(Tooltip);

export default function CoverageRatioTable(props) {
  const classes = useStyle();
  const tableRef = useRef();
  const tableContainerRef = useRef();

  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [localData, setLocalData] = useState([]);

  useEffect(() => {
    setLocalData([...props.data]);
  }, [props.data]);

  const handleEditStart = (index) => {
    setEditingCell(index);
    setEditValue(localData[index].COVERAGE_RATIO.toString());
  };

  const handleEditChange = (e) => {
    const value = e.target.value;
    
    // Allow empty string
    if (value === "") {
      setEditValue(value);
      return;
    }
    
    // Regular expression to match numbers with up to 2 decimal places
    // Allows: 123, 123., 123.4, 123.45
    // Disallows: 123.456 (more than 2 decimal places)
    const decimalRegex = /^\d*\.?\d{0,2}$/;
    
    if (decimalRegex.test(value)) {
      setEditValue(value);
    }
  };

  const handleSave = () => {
    if (editingCell === null) return;
    
    // Parse and round to 2 decimal places to ensure consistency
    let newValue = parseFloat(editValue) || 0;
    newValue = Math.round(newValue * 100) / 100; // Round to 2 decimal places
    
    const updatedData = localData.map((item, idx) => {
      if (idx === editingCell) {
        return { ...item, COVERAGE_RATIO: newValue };
      }
      return item;
    });

    setLocalData(updatedData);
    
    if (props.onChange) {
      props.onChange(updatedData);
    }

    setEditingCell(null);
    setEditValue("");
  };

  const handleCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const renderCell = (item, index) => {
    const isEditing = editingCell === index;

    if (isEditing) {
      return (
        <TableCell className={classes.editingCell} align="right">
          <TextField
            value={editValue}
            onChange={handleEditChange}
            className={classes.textField}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
              } else if (e.key === "Escape") {
                handleCancel();
              }
            }}
            onBlur={handleSave}
            InputProps={{
              disableUnderline: true,
            }}
          />
        </TableCell>
      );
    }

    return (
      <TableCell
        className={classes.editableCell}
        align="right"
        onClick={() => handleEditStart(index)}
      >
        {item.COVERAGE_RATIO}
      </TableCell>
    );
  };

  return (
    <div className={classes.root}>
      <Grid container direction="column" alignItems="center">
        <div className={classes.tableWrapper}>
          <div className={classes.copyButtonContainer}>
            <CopyTablesObject tableRef={tableRef} />
          </div>

          <TableContainer
            ref={tableContainerRef}
            className={classes.mainTableContainer}
          >
            <Table
              ref={tableRef}
              size="small"
              aria-label="coverage ratio table"
              style={{ tableLayout: "auto", width: "100%" }}
              data-test={props.dataTest}
            >
              <TableBody>
                <StyledTableRow>
                  <TableCell className={classes.repCell} align="left" style={{width: '150px', fontWeight: 600}}>
                    Current Qtr Plus
                  </TableCell>

                  {localData.map((col, idx) => {
                    return (
                      <HtmlTooltip
                        key={`CQ-${idx}`}
                        title={
                          <div
                            style={{
                              borderTop: "solid #fee599ff",
                              borderRadius: "0.5rem",
                              padding: "0px",
                              margin: 0,
                              borderWidth: "0.25rem",
                            }}
                          >
                            <div className="container">
                              <Typography
                                style={{ padding: "0.1rem" }}
                                className="heading-style"
                                align="left"
                              >
                                {`Double-click on the cell you'd like to edit`}
                              </Typography>
                            </div>
                          </div>
                        }
                      >
                        <TableCell
                          align="center"
                          className={classes.repCell}
                          style={{
                            background: idx % 2 === 0 ? "#4471c4" : "#5b9bd5",
                            color: "white",
                            fontWeight: 600
                          }}
                        >
                          <div>{col.CURRENT_QTR_PLUS}</div>
                          <div style={{ 
                            background: '#fee599', 
                            padding: '0rem 0.4rem', 
                            border: '0.5px solid lightgrey', 
                            color: 'black', 
                            textAlign: 'center', 
                            display: 'inline-block', 
                            maxWidth: '50px', 
                            fontSize: '0.85rem', 
                            fontWeight: 600, 
                            marginTop: '4px' 
                          }}>
                            Edit
                          </div>
                        </TableCell>
                      </HtmlTooltip>
                    );
                  })}
                </StyledTableRow>

                <StyledTableRow>
                  <TableCell className={classes.repCell} align="left" style={{width: '200px'}}>
                    Coverage Ratio Goal 
                  </TableCell>
                  {localData.map((col, idx) => renderCell(col, idx))}
                </StyledTableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Grid>
    </div>
  );
}